// Content for the company / marketing pages: pricing, integrations, onboarding,
// affiliate program, about, and contacts. English only — mirrors factory.online copy.

export type PricingPlan = {
  name: string;
  price: string;
  period: string;
  blurb: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
};

// Comparison matrix: "yes" = included, "no" = not included, any other string
// is rendered as a text value (e.g. limits, fees, "soon").
export type CompareRow = {
  label: string;
  values: string[];
};

export type CompareSection = {
  title: string;
  rows: CompareRow[];
};

export const PRICING = {
  intro: {
    eyebrow: "Pricing",
    title: "Choose a plan that fits your production",
    lead: "Choose the capacity that fits your operation today. Every plan runs on the same cloud platform, so your data and configuration carry forward as you scale.",
  },
  billingNote: "Prices shown in EUR per month.",
  plans: [
    {
      name: "Free",
      price: "€0",
      period: "/ month",
      blurb: "For small businesses getting started.",
      features: [
        "1 user",
        "40 items (200 for military)",
        "3 storage locations",
        "Basic sales, purchasing, production, WMS",
        "MRP, Job Orders, Standard Costing",
        "AI assistant + Knowledge Base",
      ],
      cta: "Book a demo",
    },
    {
      name: "Starter",
      price: "€99",
      period: "/ month",
      blurb: "For small businesses taking their first step out of spreadsheets.",
      features: [
        "1 user",
        "200 items (400 for military)",
        "1 storage location",
        "Sales Orders, Customers, Inventory Management",
        "BEEDIGIT MES integration",
        "AI assistant + Knowledge Base",
      ],
      cta: "Get Starter",
    },
    {
      name: "Standard",
      price: "€499",
      period: "/ month",
      blurb: "For small and medium businesses ready for full production control.",
      features: [
        "5 users",
        "500 items (1,000 for military)",
        "3 storage locations",
        "Full set of core modules: SCM, MRP, WMS, Job Orders",
        "Routings Management",
        "Standard roles & permissions",
        "Live support",
      ],
      cta: "Get Standard",
    },
    {
      name: "Pro",
      price: "€1,299",
      period: "/ month",
      blurb: "For medium businesses that need scheduling and real capacity planning.",
      features: [
        "20 users",
        "10,000 items",
        "5 storage locations",
        "Batch-tracking with expiry dates",
        "Alternative work centers",
        "Purchase Requisitions",
        "Labor Requirements Planning",
        "Rough-Cut Capacity Planning",
        "Live support",
      ],
      cta: "Get Pro",
      highlighted: true,
    },
    {
      name: "Advanced",
      price: "€1,997",
      period: "/ month",
      blurb: "For medium/large businesses running complex, multi-site production.",
      features: [
        "30 users",
        "15,000 items",
        "8 storage locations",
        "Outsourced manufacturing",
        "Actual costing",
        "Dependent demand aggregation",
        "Advanced scheduling (Gantt, changeovers)",
        "Work centre constraints",
        "API access",
      ],
      cta: "Get Advanced",
    },
    {
      name: "Enterprise",
      price: "Let's talk",
      period: "",
      blurb: "For large enterprises with dedicated support and custom needs.",
      features: [
        "Unlimited users",
        "Unlimited items",
        "Unlimited storage locations",
        "Custom roles & permissions",
        "Custom printing forms",
        "Dedicated Key Account Manager",
        "Priority support",
        "Early access to new features",
      ],
      cta: "Get a demo",
    },
  ] as PricingPlan[],

  // Feature comparison matrix — mirrors factory.online/prices table.
  // Columns: Starter, Standard, Pro, Advanced, Enterprise.
  comparePlans: ["Starter", "Standard", "Pro", "Advanced", "Enterprise"],
  compareSections: [
    {
      title: "Limits",
      rows: [
        { label: "Users, max.", values: ["1", "5", "20", "25", "unlimited"] },
        { label: "Items, max.", values: ["200", "500", "10 000", "15 000", "unlimited"] },
        { label: "Storage locations, max.", values: ["1", "3", "5", "8", "unlimited"] },
        { label: "Extra user monthly fee (billed annually)", values: ["no", "€24", "€70", "€93", "Let's talk"] },
        { label: "Extra storage location monthly fee (billed annually)", values: ["no", "€23", "€34", "€46", "Let's talk"] },
      ],
    },
    {
      title: "Sell",
      rows: [
        { label: "Sales order management", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Quotes management, materials and capacity booking", values: ["no", "no", "no", "yes", "yes"] },
        { label: "Customer contacts management", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Invoicing", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Dispatching", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Customer returns", values: ["yes", "yes", "yes", "yes", "yes"] },
      ],
    },
    {
      title: "Inventory",
      rows: [
        { label: "Inventory management", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Warehouse management (storage bin)", values: ["no", "no", "no", "yes", "yes"] },
        { label: "Batch-tracking with expiry dates", values: ["no", "no", "yes", "yes", "yes"] },
        { label: "Additional costs (services)", values: ["no", "no", "no", "yes", "yes"] },
      ],
    },
    {
      title: "Make",
      rows: [
        { label: "Master and Product data management", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Manufacturing Routings", values: ["no", "no", "yes", "yes", "yes"] },
        { label: "Matrix Bill of Materials", values: ["no", "yes", "yes", "yes", "yes"] },
        { label: "Outsourced Manufacturing", values: ["no", "no", "no", "yes", "yes"] },
        { label: "Alternative work centers management", values: ["no", "no", "yes", "yes", "yes"] },
        { label: "Rough Cut Capacity Planning (RCCP)", values: ["no", "no", "yes", "yes", "yes"] },
        { label: "Labor Requirements Planning", values: ["no", "no", "yes", "yes", "yes"] },
        { label: "Job Orders, Output and consumption confirmations", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Standard costing (direct)", values: ["no", "yes", "yes", "yes", "yes"] },
        { label: "Actual costing (direct)", values: ["no", "no", "no", "yes", "yes"] },
      ],
    },
    {
      title: "Buy",
      rows: [
        { label: "Material Resources Planning (MRP)", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Purchase orders management", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Purchase requisitions management", values: ["no", "no", "yes", "yes", "yes"] },
        { label: "Safety stock levels management", values: ["yes", "yes", "yes", "yes", "yes"] },
      ],
    },
    {
      title: "Advanced Production",
      rows: [
        { label: "Dependent demand aggregation", values: ["no", "no", "no", "yes", "yes"] },
        { label: "Scheduling (Gantt, Changeovers, Job orders grouping)", values: ["no", "no", "no", "yes", "yes"] },
        { label: "Work Centers constraints management", values: ["no", "no", "no", "yes", "yes"] },
        { label: "Sales & Operations Planning", values: ["no", "no", "no", "soon", "soon"] },
      ],
    },
    {
      title: "Integrations",
      rows: [
        { label: "BEEDIGIT MES/OEE integration", values: ["no", "yes", "yes", "yes", "yes"] },
        { label: "Accounting systems connectors", values: ["no", "no", "yes", "yes", "yes"] },
        { label: "API access", values: ["no", "no", "no", "yes", "yes"] },
        { label: "PowerBI reports", values: ["no", "yes", "yes", "yes", "yes"] },
      ],
    },
    {
      title: "Access and printing forms",
      rows: [
        { label: "Standard user roles and permissions", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Custom user roles and permissions", values: ["no", "no", "no", "no", "yes"] },
        { label: "Standard printing forms", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "Custom printing forms", values: ["no", "no", "no", "no", "yes"] },
      ],
    },
    {
      title: "Support",
      rows: [
        { label: "Knowledge base", values: ["yes", "yes", "yes", "yes", "yes"] },
        { label: "AI assistant support", values: ["no", "yes", "yes", "yes", "yes"] },
        { label: "Live support", values: ["no", "no", "yes", "yes", "yes"] },
        { label: "Dedicated key account manager", values: ["no", "no", "no", "no", "yes"] },
        { label: "Priority support", values: ["no", "no", "no", "no", "yes"] },
        { label: "New features early access", values: ["no", "no", "no", "no", "yes"] },
      ],
    },
  ] as CompareSection[],

  comparisonNotes: [
    "Extra users and storage locations can be added to any paid plan for a monthly fee.",
    "All plans include the same core sales, inventory and production modules — higher tiers unlock more capacity, automation and scheduling depth.",
    "Book a tailored demo to validate the fit against your production processes before choosing a plan.",
  ],
  faq: [
    {
      q: "Can I switch plans later?",
      a: "Yes. You can upgrade at any time as your team, item catalogue or number of sites grows — your data and configuration carry over.",
    },
    {
      q: "Can we see the system before choosing a plan?",
      a: "Yes. Book a tailored demo and our manufacturing consultants will walk through the relevant workflows using your production scenario.",
    },
    {
      q: "What happens if I exceed my plan's limits?",
      a: "You can add extra users or storage locations for a monthly fee, or upgrade to the next tier — nothing is disabled abruptly.",
    },
    {
      q: "Do prices include support?",
      a: "Standard and above include live support; Advanced and Enterprise add priority response times and, for Enterprise, a dedicated Key Account Manager.",
    },
    {
      q: "How is billing handled?",
      a: "Listed prices are in EUR per month. Pricing can vary by region and the correct regional amount is shown for each language version.",

    },
  ],
};

export const INTEGRATIONS = {
  intro: {
    eyebrow: "Integrations",
    title: "Connect factory.online to the systems you already use",
    lead: "Connect the systems your factory already uses, keep orders and production statuses moving automatically, and remove spreadsheet handoffs from daily work.",
  },
  groups: [
    {
      category: "Shop floor & equipment",
      items: [
        {
          name: "BEEDIGIT",
          description:
            "Real-time production tracking. Job Orders are sent directly to operator tablets, while performance, downtime and output data sync back to calculate actual production costs and optimise planning accuracy.",
        },
      ],
    },
    {
      category: "ERP & finance",
      items: [
        {
          name: "Microsoft Dynamics 365",
          description:
            "Finance, accounting and production finally speak the same language. Orders, purchases, payments and accounting in Dynamics 365 are automatically synchronised with production processes in factory.online.",
        },
        {
          name: "SAP",
          description:
            "SAP runs the business, we run production. The integration connects finance and accounting with real manufacturing processes, so management decisions are based on accurate shop floor data.",
        },
      ],
    },
    {
      category: "Analytics & reporting",
      items: [
        {
          name: "Power BI",
          description:
            "Numbers don't work on their own — they need to be seen. Integration with Power BI transforms production data from factory.online into clear dashboards and charts.",
        },
      ],
    },
    {
      category: "Printing & documents",
      items: [
        {
          name: "PrintNode",
          description:
            "Print labels and documents without extra clicks. Integration with PrintNode automates printing directly from the factory.online cloud — documents print exactly where and when they're needed.",
        },
      ],
    },
    {
      category: "Coming soon",
      items: [
        {
          name: "Warehousing",
          description: "Deeper warehouse execution integrations are on the roadmap.",
        },
      ],
    },
  ],
  capabilities: [
    {
      title: "Data moves between systems by itself",
      text: "Orders and production statuses reach the right system without being copied by hand.",
      plan: "Advanced",
    },
    {
      title: "Move thousands of records without row-by-row work",
      text: "Synchronize large catalogs and operational documents without another Excel chain.",
      plan: "Advanced",
    },
    {
      title: "Different partners, different exchange rules",
      text: "Keep each external connection on the workflow and data scope it actually needs.",
      plan: "Enterprise",
    },
  ],
  closing: {
    title: "Didn't find the integration that gives you superpowers?",
    lead: "We're always open to new integrations. Tell us what you're missing, and we'll find a solution.",
  },
};

export const ONBOARDING = {
  intro: {
    eyebrow: "Onboarding",
    title: "factory.online Onboarding — a smooth, guided start",
    lead: "Launching a new system shouldn't be painful. Forget long, exhausting implementations with an army of expensive consultants. We are not a consulting bureau — we are your guide to a fast launch.",
  },
  stats: [
    { value: "45–120", label: "Days from start to results" },
    { value: "5×", label: "Faster than traditional ERPs that take years to implement" },
    { value: "100%", label: "A transparent plan — no surprises, only clear steps" },
  ],
  included: [
    {
      title: "Personal onboarding manager",
      text: "Guides you at every step, from kickoff to go-live.",
    },
    {
      title: "Expert support",
      text: "For migrating your data into factory.online without disruption.",
    },
    {
      title: "Personal onboarding plan",
      text: "Guidance and control at every step to success.",
    },
    {
      title: "Regular workshops",
      text: "Structured sessions that move your team closer to results.",
    },
  ],
  steps: [
    {
      day: "Day 1",
      title: "Onboarding start",
      text: "First login and introduction to processes.",
    },
    {
      day: "Day 14",
      title: "Data setup",
      text: "Upload data, structure products and materials.",
    },
    {
      day: "Day 30",
      title: "Process optimization",
      text: "Check your routes, optimize production.",
    },
    {
      day: "Day 60",
      title: "Integration setup",
      text: "Add integrations, validate end-to-end processes, and start operations.",
    },
    {
      day: "Day 120",
      title: "Confident results",
      text: "The team works independently and confidently in the system, and you see the results.",
    },
  ],
  testimonials: [
    {
      quote:
        "We were afraid it would be another 'ERP implementation for years.' But in two months, the team was already working in the system.",
      name: "Liam B.",
      role: "Planner",
    },
    {
      quote:
        "Onboarding with factory.online feels like a level-based game. You clearly know what's next, and the team doesn't panic because results show up almost immediately.",
      name: "Noah W.",
      role: "Process Engineer",
    },
    {
      quote:
        "We previously spent more than a year implementing another system and never launched. Here — 4 months, and the process really works.",
      name: "Oliver S.",
      role: "Supervisor",
    },
  ],
  closing: {
    title: "120 days to achieve control over the shop floor. Are you still counting in years?",
  },
};

export const AFFILIATE = {
  intro: {
    eyebrow: "Partnership program",
    title: "Partnership with factory.online",
    lead: "The success of digital transformation depends not only on technology but also on those who help businesses turn change into reality. That's why we are building a partnership ecosystem. Our partners are trusted guides who bring businesses not just a tool, but results: growth, transparency and control.",
  },
  formats: [
    {
      name: "Reseller",
      description:
        "Our trusted resellers are more than sellers. They enter businesses to show that factory.online is a real tool for change and a foundation for growth. They explain value, prove benefits with numbers, and stay involved because they are invested in long-term success.",
      benefits: [
        "Tools to \"speak the language of business,\" not just IT",
        "Full support from the factory.online team during and after sales",
        "Marketing materials and access to real case studies",
        "Synergy: client success = reseller success",
      ],
    },
    {
      name: "Implementation Partner",
      description:
        "factory.online consultants and integrators deliver more than software. They stitch technology into reality and turn a tool into a true growth driver, speaking your industry's language.",
      benefits: [
        "Access to API, documentation, and direct expert support",
        "Training based on industry-specific cases",
        "Ability to build custom integrations and additional services",
        "Joint projects, case studies and promotions with factory.online",
        "The reputation of being the trusted partner businesses rely on",
      ],
    },
  ],
  clientBenefits: [
    {
      title: "Verified experts",
      text: "We work only with trusted partners.",
    },
    {
      title: "Business language",
      text: "Partners demonstrate how factory.online impacts results, not just processes.",
    },
    {
      title: "Local presence",
      text: "Combined with industry expertise, clients receive support in their own country, tailored to their market specifics.",
    },
    {
      title: "Guaranteed results",
      text: "ISO certifications confirm the system's reliability and high level of security.",
    },
  ],
  howItWorks: [
    "Fill out the short partnership application form.",
    "The factory.online team contacts you to discuss the partnership format that fits your expertise.",
    "Get onboarded with tools, training and support to start bringing results to clients.",
  ],
  apply: {
    title: "How to become a partner",
    lead: "We are open to collaborating with companies that want to make their clients' businesses stronger. Fill out the short application form, and the factory.online team will contact you to discuss the partnership format.",
    cta: "Apply for partnership",
  },
};

export const ABOUT = {
  intro: {
    eyebrow: "About us",
    title: "We are manufacturing experts who understand your pain points",
    lead: "Our journey began in manufacturing automation more than 13 years ago. During this time, we have implemented solutions for dozens of enterprises across various industries, and today we build modern cloud services for manufacturers worldwide.",
  },
  story: [
    "Our journey began in manufacturing automation more than 13 years ago. During this time, we have implemented solutions for dozens of enterprises across various industries, and today we build modern cloud services for manufacturers worldwide.",
    "We believe that AI-powered Industry 4.0 solutions can be accessible and effective, without the unnecessary costs of complex ERPs. That is why we created factory.online.",
    "We combine manufacturing expertise with modern technology to give businesses what they seek: control, speed and profitability.",
  ],
  stats: [
    { value: "15–30%", label: "Reduction in production costs" },
    { value: "20–30%", label: "Reduction in order lead times" },
    { value: "13+ years", label: "In manufacturing automation" },
    { value: "100%", label: "Process transparency at global standards" },

  ],
  values: [
    {
      title: "Helps manufacturers grow faster",
      text: "A plug-and-play platform that pays for itself quickly, without a multi-year rollout.",
    },
    {
      title: "Minimizes errors through automation",
      text: "Fewer manual calculations, fewer spreadsheets, fewer costly mistakes.",
    },
    {
      title: "Provides real-time transparency",
      text: "Owners and executives see what's really happening on the shop floor, as it happens.",
    },
    {
      title: "Organizes chaotic processes",
      text: "Traditionally messy manufacturing workflows become structured and repeatable.",
    },
  ],
  points: [
    "13+ years of experience in manufacturing consulting",
    "English and Arabic-speaking support team",
    "Modern, accessible cloud services for manufacturers that just work",
    "Transparency, speed and real-time control",
    "We help businesses grow faster and make fewer mistakes",
  ],
};

export const CONTACTS = {
  intro: {
    eyebrow: "Contacts",
    title: "Talk to the factory.online team",
    lead: "Have an idea or a question? Let's discuss how factory.online can streamline your inventory and production management.",
  },
  email: "office@factory.online",
  reasons: [
    {
      title: "Sales & product questions",
      text: "Get answers to all your questions from inventory and production experts, and see how factory.online helps maintain optimal stock levels with real-time updates.",
    },
    {
      title: "Get a demo",
      text: "Book a walkthrough of factory.online tailored to your production process.",
    },
    {
      title: "Plan your implementation",
      text: "Learn about the implementation process and next steps for a fast start with the platform.",
    },
    {
      title: "Partnership program",
      text: "Explore becoming a reseller or implementation partner and bring factory.online to your clients.",
    },
    {
      title: "Customer support",
      text: "Existing customers: reach us through the in-app Support, or check the Knowledge Base for guides.",
    },
  ],
  offices: [
    {
      name: "Headquarters",
      address: "Dubai Internet City, Dubai, United Arab Emirates",
    },
  ],
};

export const REUSING_CONTENT = {
  intro: {
    eyebrow: "Copyright",
    title: "Reusing content from factory.online",
    lead: "We love to share our knowledge — and we want it used fairly. Here is what you may do with the articles, guides and illustrations on this website.",
  },
  sections: [
    {
      title: "Using content inside your company",
      text: "You are welcome to use articles from this website within your company — for example as training or onboarding materials. All we ask is that you cite factory.online and our copyright in those materials.",
    },
    {
      title: "Republishing and commercial use",
      text: "If you want to republish our content publicly or use it commercially in any other way, please contact us first. Typically all we require is that you cite factory.online, note the copyright, and include a link back to the original article.",
    },
    {
      title: "Our content is protected",
      text: "All articles, guides and illustrations on factory.online are our intellectual property and are protected by copyright law. We regularly check the web for unauthorized copies of our content and may request their removal or proper attribution.",
    },
  ],
  closing: {
    title: "A question about our content?",
    lead: "Write to us — we are happy to share, as long as authorship is respected.",
  },
};
